import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AcModel } from 'projects/core/src/lib/models/ac.model';
import { LocationModel } from 'projects/core/src/lib/models/location.model';
import { RoomModel } from 'projects/core/src/lib/models/room.model';
import { TemplateModel } from 'projects/core/src/lib/models/template.model';
import { UserModel } from 'projects/core/src/lib/models/user.model';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { DetailModes } from 'projects/admin/src/app/shared/constants';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import * as moment from 'moment';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';
import { BusinessunitModel } from 'projects/core/src/lib/models/businessunit.model';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';

const DISABLED = {
  planned: [],
  running: ['lqgposition', 'dispatcher', 'location', 'room1', 'room2', 'location_other.street', 'location_other.city', 'location_other.zip', 'location_other.room_main', 'location_other.room_preparation', 'conference_url', 'conference_info', 'prebriefing_url', 'prebriefing_info', 'tz'],
  finished: ['lqgposition', 'dispatcher', 'location', 'room1', 'room2', 'location_other.street', 'location_other.city', 'location_other.zip', 'location_other.room_main', 'location_other.room_preparation', 'conference_url', 'conference_info', 'prebriefing_url', 'prebriefing_info', 'informations_moderator', 'tz'],
  cancelled: ['lqgposition', 'dispatcher', 'location', 'room1', 'room2', 'location_other.street', 'location_other.city', 'location_other.zip', 'location_other.room_main', 'location_other.room_preparation', 'conference_url', 'conference_info', 'prebriefing_url', 'prebriefing_info', 'informations_moderator', 'tz'],
};

const FIELD_FALLBACK = [
  'start_time',
  'location',
  'room1',
  'room2',
  'location_other',
  'location_other.street',
  'location_other.city',
  'location_other.zip',
  'location_other.room_main',
  'location_other.room_preparation'
];

@Component({
  selector: 'app-order-detail-orderdata',
  templateUrl: './order-detail-orderdata.component.html',
  styleUrls: ['./order-detail-orderdata.component.scss']
})
export class OrderDetailOrderdataComponent implements OnInit, OnChanges, OnDestroy {

  @Input() form: UntypedFormGroup;
  @Input() options: { [key: string]: Array<any> };
  @Input() templates: Array<TemplateModel>;
  @Input() businessunit: BusinessunitModel | null = null;
  @Input() texts: { [key: string]: string };
  @Input() order: AcModel = null;
  @Input() mode: DetailModes | null;
  @Input() inEdit: boolean;
  @Input() submitting: boolean;

  @Output() changed = new EventEmitter<TemplateModel>();

  public roomsMain: Array<RoomModel> = [];
  public roomsPreparation: Array<RoomModel> = [];
  public locations: Array<LocationModel> = [];

  public country: string | null = null;
  public timezone: string | null = null;
  public lqgPositionVisible = false;
  public minDate = null;

  public tagVisible = false;
  public tags: Array<string> = [];
  public tagLabel = null;
  public tagDescription = null;

  private subForm: UntypedFormGroup = this.fb.group({
    template: new UntypedFormControl('', [Validators.required]),
    tags: new UntypedFormControl(''),
    dispatcher: new UntypedFormControl(''),
    location: new UntypedFormControl(''),
    room1: new UntypedFormControl(''),
    room2: new UntypedFormControl(''),
    conference_url: new UntypedFormControl('', [CustomValidators.url()]),
    conference_info: new UntypedFormControl(''),
    prebriefing_url: new UntypedFormControl('', [CustomValidators.url()]),
    prebriefing_info: new UntypedFormControl(''),
    start_time: new UntypedFormControl('', [Validators.required]),
    tz: this.fb.group({}),
    until: new UntypedFormControl({value: '', disabled: true}),
    reportdate: new UntypedFormControl(''),
    lqgposition: new UntypedFormControl(''),
    informations_internal: new UntypedFormControl(''),
    informations_moderator: new UntypedFormControl(''),
    location_other: this.fb.group({
      street: new UntypedFormControl(''),
      city: new UntypedFormControl(''),
      zip: new UntypedFormControl(''),
      room_main: new UntypedFormControl(''),
      room_preparation: new UntypedFormControl(''),
    })
  });
  private subscriptions: Array<Subscription> = [];

  constructor(
    private fb: UntypedFormBuilder,
    private helper: HelperService,
    private transloco: TranslocoService,
    private auth: AuthStateService
  ) { }

  ngOnInit(): void {
    this.form.addControl('orderdata', this.subForm);
    this.subscriptions.push(this.subForm.get('template').valueChanges.subscribe((value: TemplateModel) => {
      this.setLocations();
      this.setLqgPosition();
      this.setTags();
      this.changed.emit(value);
    }));
    this.subscriptions.push(this.subForm.get('start_time').valueChanges.subscribe(() => {
      this.setUntil();
    }));
    this.subscriptions.push(this.subForm.get('location').valueChanges.subscribe(() => {
      this.setRoomsAndCountry();
    }));
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.mode === DetailModes.edit) {
      this.subForm.get('template').disable();
    }
    if (
      this.order?.status === 'planned' ||
      this.order?.status === 'running' ||
      this.order?.status === 'finished' ||
      this.order?.status === 'cancelled'
    ) {
      this.subForm.get('start_time').disable();
      this.subForm.get('location').disable();
      this.subForm.get('room1').disable();
      this.subForm.get('room2').disable();
      this.subForm.get('location_other.street').disable();
      this.subForm.get('location_other.city').disable();
      this.subForm.get('location_other.zip').disable();
      this.subForm.get('location_other.room_main').disable();
      this.subForm.get('location_other.room_preparation').disable();
      this.minDate = null;
    } else {
      this.minDate = moment().startOf('day').toDate();
    }
    // fallback if fields are not set
    if (this.order?.status === 'planned') {
      FIELD_FALLBACK.forEach((field: string) => {
        if (this.subForm.get(field).value === '' || this.subForm.get(field).value === null) {
          this.subForm.get(field).enable();
        }
      });
    }
    if (changes.order || changes.inEdit) {
      this.initForm();
      this.setValidations();
      FormHelper.disableForm(this.subForm, this.order?.status, DISABLED);
    }
    if (changes.businessunit) {
      this.setLocations();
    }
  }

  ngOnDestroy(): void {
    this.helper.unsetSubscriptions(this.subscriptions);
  }

  public getPayload(): any {
    return {
      template: this.subForm.get('template').value?.id,
      tags: this.subForm.get('tags').value,
      dispatcher: this.subForm.get('dispatcher').value?.id,
      location: this.subForm.get('location').value?.id,
      room1: this.subForm.get('room1').value?.id,
      room2: this.subForm.get('room2').value?.id,
      conference_url: this.subForm.get('conference_url').value,
      conference_info: this.subForm.get('conference_info').value,
      prebriefing_url: this.subForm.get('prebriefing_url').value,
      prebriefing_info: this.subForm.get('prebriefing_info').value,
      start_time: (this.subForm.get('start_time').value) ? moment(this.subForm.get('start_time').value).format('YYYY-MM-DD HH:mm:ss') : null,
      country: (this.isVirtual() || this.isOtherLocation()) ? this.country : null,
      timezone: (this.isVirtual() || this.isOtherLocation()) ? this.timezone : null,
      reportdate: (this.subForm.get('reportdate').value) ? moment(this.subForm.get('reportdate').value).format('YYYY-MM-DD') : null,
      informations_internal: this.subForm.get('informations_internal').value,
      informations_moderator: this.subForm.get('informations_moderator').value,
      lqgposition: this.subForm.get('lqgposition').value,
      location_other: {
        street: this.subForm.get('location_other.street').value,
        city: this.subForm.get('location_other.city').value,
        zip: this.subForm.get('location_other.zip').value,
        room_main: this.subForm.get('location_other.room_main').value,
        room_preparation: this.subForm.get('location_other.room_preparation').value,
      },
    };
  }

  public isVirtual(): boolean {
    const location = this.subForm.get('location').value;
    return !!location?.virtual;
  }

  public isOtherLocation(): boolean {
    const location = this.subForm.get('location').value;
    return !!location?.other;
  }

  public hasRooms(): boolean {
    const location = this.subForm.get('location').value;
    return Array.isArray(location?.rooms) && location.rooms.length > 0;
  }

  public getDstLabel(): string {
    return ' (' + this.helper.getTimezoneName(this.timezone, this.subForm.get('start_time').value) + ')';
  }

  private initForm(): void {
    if (this.order) {
      const startTime = this.order.start_time_admin;
      const lqgposition = this.order.misc?.order?.lqgposition;
      const informations_internal = this.order.informations_internal;
      const informations_moderator = this.order.informations_moderator;
      const reportdate = this.order.misc?.order?.reportdate;
      this.subForm.patchValue({
        template: this.getInitTemplate() || '',
        dispatcher: this.getInitDispatcher() || '',
        location: this.getInitLocation() || '',
        room1: this.getInitRoom('main') || '',
        room2: this.getInitRoom('preparation') || '',
        conference_url: this.order.conference_url,
        conference_info: this.order.conference_info,
        prebriefing_url: this.order.prebriefing_url,
        prebriefing_info: this.order.prebriefing_info,
        start_time: (startTime) ? moment(startTime).toDate() : '',
        lqgposition: lqgposition || '',
        informations_internal: informations_internal || '',
        informations_moderator: informations_moderator || '',
        reportdate: (reportdate) ? moment(reportdate).toDate() : '',
        location_other: this.order.location_other
      });
      this.country = this.order.country;
      this.timezone = this.order.timezone;
    }
  }

  public getTemplateName(): string {
    return this.templates.find((template: TemplateModel) => {
      return template.id === this.order?.template_id;
    })?.name || '';
  }

  public getLqgPositionName(): string {
    const value = this.order?.misc?.order?.lqgposition;
    return this.options.lqg_positions?.find(option => option.value === value)?.label || '';
  }

  public getMainRoomName(): string {
    return this.getInitRoom('main')?.name || '';
  }

  public getPreprationRoomName(): string {
    return this.getInitRoom('preparation')?.name || '';
  }

  public getOtherLocationName(): string {
    return this.options?.locations?.find((location: LocationModel) => {
      return location.other === true;
    }).name || '';
  }

  public hasPrebriefing(): boolean {
    if (this.order) {
      return this.order.prebriefing === true || this.order.template?.settings?.prebriefing === true;
    } else {
      const template = this.subForm.get('template').value;
      return template?.settings?.prebriefing === true;
    }
  }

  private getInitTemplate(): TemplateModel {
    return this.templates.find((template: TemplateModel) => {
      return template.id === this.order?.template_id;
    });
  }

  private getInitDispatcher(): UserModel {
    return this.options?.dispatchers?.find((dispatcher: UserModel) => {
      return dispatcher.id === this.order?.dispatcher_id;
    });
  }

  private getInitLocation(): LocationModel {
    return this.options?.locations?.find((location: LocationModel) => {
      return (this.order?.location_id !== null) ? location.id === this.order?.location_id : location.other === true;
    });
  }

  private getInitRoom(type: string): RoomModel {
    const orderRoom = this.order?.rooms.find((room: RoomModel) => {
      return room.type === type;
    });
    if (orderRoom !== undefined) {
      return this.options?.rooms.find((room: RoomModel) => {
        return room.id === orderRoom.id;
      });
    }
  }

  private setLocations(): void {
    const template = this.subForm.get('template').value;
    if (template !== undefined) {
      let locations = (this.options?.locations || []).map((location: any) => {
        location.name = (location.name?.includes('common.')) ? this.transloco.translate(location.name) : location.name;
        return new LocationModel().deserialize(location);
      }).filter((location: LocationModel) => {
        // filter on onsite/virtual
        let onsite = template.onsite;
        let virtual = template.virtual;
        if (this.businessunit && template.businessunits?.length > 0) {
          // overwrite template settings with businessunit settings
          const businessunit = template.businessunits.find(actual => actual.id === this.businessunit.id);
          if (businessunit?.pivot?.settings?.['onsite'] !== undefined) {
            onsite = !!businessunit.pivot.settings['onsite'];
          }
          if (businessunit?.pivot?.settings?.['virtual'] !== undefined) {
            virtual = !!businessunit.pivot.settings['virtual'];
          }
        }
        return (onsite === true && location.virtual !== true) || (virtual === true && location.virtual === true);
      }).filter((location: LocationModel) => {
        // fiter on location
        return template.locations.length === 0 || template.locations?.find(actual => actual.id === location.id) !== undefined;
      });
      if (
        this.order?.status !== undefined &&
        this.order?.status !== 'requested' &&
        this.order?.location !== undefined &&
        this.order?.location !== null
      ) {
        locations = locations.filter((location: LocationModel) => {
          return location.virtual === this.order?.location.virtual;
        });
      }
      this.locations = locations;
      if (this.locations.length === 1) {
        setTimeout(() => {
          this.subForm.get('location').setValue(this.locations[0]);
        }, 100);
      }
    }
  }

  private setRoomsAndCountry(location?: LocationModel): void {
    this.roomsMain = [];
    this.roomsPreparation = [];
    const orderLocation = this.subForm.get('location').value || location;
    if (orderLocation) {
      if (orderLocation.virtual === true || orderLocation.other === true) {
        this.setRoomValidation(false);
        this.setCountryValidation(true);
        if (orderLocation?.other === true) {
          this.setOtherValidation(true);
        } else {
          this.setOtherValidation(false);
        }
      } else {
        this.setRoomValidation(orderLocation?.rooms?.length > 0);
        this.setCountryValidation(false);
        this.setOtherValidation(false);
        if (orderLocation?.rooms) {
          orderLocation.rooms.forEach((room: RoomModel) => {
            if (room.type === 'main') {
              this.roomsMain.push(room);
            } else if (room.type === 'preparation') {
              this.roomsPreparation.push(room);
            }
          });
        }
      }
    }
  }

  private setTags(): void {
    const lang = this.transloco.getActiveLang().toUpperCase();
    const template = this.subForm.get('template').value || null;
    if (this.auth.hasFeature('templates.tags') && template?.tags?.length > 0) {
      this.tags = template.tags;
      this.tagVisible = true;
      this.tagLabel = template.tag_label?.[lang || 'DE'] || this.transloco.translate('common.labels.tags');
      this.tagDescription = template.tag_description?.[lang || 'DE'] || null;
      if (this.subForm.get('tags').value === null || this.subForm.get('tags').value === '') {
        this.subForm.get('tags').setValue(this.order?.tags);
      }
    } else {
      this.tags = [];
      this.tagVisible = false;
      this.tagLabel = null;
      this.tagDescription = null;
      this.subForm.get('tags').setValue(null);
    }

  }

  private setValidations(): void {
    if (this.order?.status !== 'requested') {
      this.subForm.get('location').setValidators([Validators.required]);
      this.subForm.get('dispatcher').setValidators([Validators.required]);
    } else {
      this.subForm.get('location').clearValidators();
      this.subForm.get('dispatcher').clearValidators();
    }
  }

  private setRoomValidation(validate: boolean): void {
    if (this.order?.status !== 'requested' && validate === true) {
      this.subForm.get('room1').setValidators([Validators.required]);
      this.subForm.get('room2').setValidators([Validators.required]);
    } else {
      this.subForm.get('room1').setValue('');
      this.subForm.get('room1').clearValidators();
      this.subForm.get('room2').setValue('');
      this.subForm.get('room2').clearValidators();
    }
    this.subForm.get('room1').updateValueAndValidity();
    this.subForm.get('room2').updateValueAndValidity();
  }

  private setCountryValidation(validate: boolean): void {
    if (this.subForm.get('tz.country')) {
      if (this.order?.status !== 'requested' && validate === true) {
        this.subForm.get('tz.country').setValidators([Validators.required]);
      } else {
        this.subForm.get('tz.country').clearValidators();
      }
      this.subForm.get('tz.country').updateValueAndValidity();
    }
  }

  private setOtherValidation(validate: boolean): void {
    if (this.order?.status !== 'requested' && validate === true) {
        this.subForm.get('location_other.city').setValidators([Validators.required]);
        this.subForm.get('location_other.room_main').setValidators([Validators.required]);
    } else {
      this.subForm.get('location_other.city').clearValidators();
      this.subForm.get('location_other.room_main').clearValidators();
    }
    this.subForm.get('location_other.city').updateValueAndValidity();
    this.subForm.get('location_other.room_main').updateValueAndValidity();
  }

  private setUntil(): void {
    let until = '';
    const startValue = this.subForm.get('start_time').value;
    if (startValue) {
      let duration = 0;
      if (this.order) {
        duration = this.order.duration;
      } else if (this.subForm.get('template').value?.duration) {
        duration = this.subForm.get('template').value?.duration;
      }
      until = moment(startValue).add(duration, 'minutes').format('HH:mm');
    }
    this.subForm.get('until').setValue(until);
  }

  public setLqgPosition(): void {
    const template = this.templates.find((atualTemplate: TemplateModel) => {
      return atualTemplate.id === this.subForm.get('template').value?.id;
    });
    if (template !== undefined && template.settings?.lqgposition === true) {
      this.lqgPositionVisible = true;
      // this.subForm.get('lqgposition').setValidators([Validators.required]);
    } else {
      this.subForm.get('lqgposition').clearValidators();
      this.lqgPositionVisible = false;
    }
    this.subForm.get('lqgposition').updateValueAndValidity();
  }

}
